import { z } from 'zod';

const IdSchema = z.string().min(1).max(160);

export const SdkFieldTypeSchema = z.enum([
  'text',
  'textarea',
  'number',
  'boolean',
  'date',
  'datetime',
  'select',
  'multi-select',
  'email',
  'url',
  'phone',
  'json',
]);
export type SdkFieldType = z.infer<typeof SdkFieldTypeSchema>;

export const SdkFieldDefinitionSchema = z.object({
  fieldId: IdSchema,
  label: z.string().min(1).max(160),
  type: SdkFieldTypeSchema,
  required: z.boolean().default(false),
  description: z.string().max(500).optional(),
  options: z.array(z.string().min(1).max(160)).default([]),
  unique: z.boolean().default(false),
  indexed: z.boolean().default(false),
  defaultValue: z.unknown().optional(),
});
export type SdkFieldDefinition = z.infer<typeof SdkFieldDefinitionSchema>;

export const SdkStatusPolicySchema = z.object({
  defaultStatus: z.enum(['draft', 'active', 'inactive', 'archived']).default('draft'),
  allowedStatuses: z.array(z.enum(['draft', 'active', 'inactive', 'archived'])).min(1)
    .default(['draft', 'active', 'inactive', 'archived']),
  allowArchive: z.boolean().default(true),
  approvalRequiredForBulkChange: z.boolean().default(false),
});
export type SdkStatusPolicy = z.infer<typeof SdkStatusPolicySchema>;

export const SdkDateTrackingPolicySchema = z.object({
  enableEffectiveAt: z.boolean().default(true),
  enableExpiresAt: z.boolean().default(true),
  enablePublishedAt: z.boolean().default(false),
  enableArchivedAt: z.boolean().default(true),
});
export type SdkDateTrackingPolicy = z.infer<typeof SdkDateTrackingPolicySchema>;

export const SdkResourceSchema = z.object({
  resourceId: IdSchema,
  label: z.string().min(1).max(160),
  tableName: z.string().min(1).max(160),
  description: z.string().max(1000).optional(),
  fields: z.array(SdkFieldDefinitionSchema).min(1),
  statusPolicy: SdkStatusPolicySchema.default({}),
  dateTrackingPolicy: SdkDateTrackingPolicySchema.default({}),
  enableImportExport: z.boolean().default(true),
  enableSavedViews: z.boolean().default(true),
});
export type SdkResource = z.infer<typeof SdkResourceSchema>;

export const SdkCrudManifestSchema = z.object({
  manifestId: IdSchema,
  version: z.string().min(1).max(80),
  resources: z.array(SdkResourceSchema).min(1),
});
export type SdkCrudManifest = z.infer<typeof SdkCrudManifestSchema>;

export const SdkSavedViewSchema = z.object({
  id: IdSchema,
  label: z.string().min(1).max(160),
  filter: z.record(z.unknown()).default({}),
});
export type SdkSavedView = z.infer<typeof SdkSavedViewSchema>;

export const SdkAgenticPromptContextSchema = z.object({
  prompt: z.string().min(1).max(4000),
  businessContext: z.string().max(4000).optional(),
  targetAudience: z.array(z.enum(['ops-admin', 'finance-admin', 'customer-admin', 'solution-admin', 'ai-agent']))
    .default(['solution-admin']),
  importSourceHint: z.enum(['none', 'csv', 'xlsx', 'api', 'unknown']).default('unknown'),
});
export type SdkAgenticPromptContext = z.infer<typeof SdkAgenticPromptContextSchema>;

export const SdkAgenticResourceDraftContractSchema = z.object({
  draftId: IdSchema,
  promptContext: SdkAgenticPromptContextSchema,
  proposedResource: SdkResourceSchema,
  proposedViews: z.array(z.enum(['list', 'detail', 'form', 'grid'])).min(1).default(['list', 'form']),
  confidenceScore: z.number().min(0).max(1).default(0.6),
  assumptions: z.array(z.string().min(1).max(500)).default([]),
  missingInformation: z.array(z.string().min(1).max(500)).default([]),
  rationale: z.array(z.string().min(1).max(500)).min(1),
  warnings: z.array(z.string().min(1).max(500)).default([]),
});
export type SdkAgenticResourceDraftContract = z.infer<typeof SdkAgenticResourceDraftContractSchema>;

export const SdkAgenticTestCaseSchema = z.object({
  caseId: IdSchema,
  label: z.string().min(1).max(160),
  kind: z.enum(['happy-path', 'validation', 'edge', 'import', 'status-transition']),
  description: z.string().max(500).optional(),
});
export type SdkAgenticTestCase = z.infer<typeof SdkAgenticTestCaseSchema>;

export const SdkAgenticTestPlanContractSchema = z.object({
  planId: IdSchema,
  resourceId: IdSchema,
  sampleRows: z.array(z.record(z.unknown())).default([]),
  testCases: z.array(SdkAgenticTestCaseSchema).min(1),
});
export type SdkAgenticTestPlanContract = z.infer<typeof SdkAgenticTestPlanContractSchema>;

export const SdkAgenticPatchProposalContractSchema = z.object({
  proposalId: IdSchema,
  resourceId: IdSchema,
  proposalKind: z.enum(['schema', 'view', 'import-mapping', 'status-policy']),
  summary: z.string().min(1).max(500),
  changes: z.array(z.object({
    path: z.string().min(1).max(240),
    action: z.enum(['add', 'update', 'remove']),
    before: z.unknown().optional(),
    after: z.unknown().optional(),
  })).min(1),
  approvalRequired: z.boolean().default(true),
});
export type SdkAgenticPatchProposalContract = z.infer<typeof SdkAgenticPatchProposalContractSchema>;

export const SdkReviewedResourceContractSchema = z.object({
  reviewId: IdSchema,
  resource: SdkResourceSchema,
  migrationSql: z.string().min(1),
  savedViews: z.array(SdkSavedViewSchema).default([]),
  reviewStatus: z.enum(['drafted', 'reviewed', 'submitted', 'approved', 'rejected']).default('reviewed'),
  reviewedAt: z.string().min(1),
});
export type SdkReviewedResourceContract = z.infer<typeof SdkReviewedResourceContractSchema>;

export const SdkResourceTemplateSchema = z.object({
  templateId: IdSchema,
  label: z.string().min(1).max(160),
  description: z.string().min(1).max(500),
  market: z.enum(['global', 'id', 'my', 'sg']).default('global'),
  profile: z.string().min(1).max(160),
  vertical: z.string().min(1).max(160),
  horizontal: z.string().min(1).max(160).default('sdk-data'),
  aiNative: z.boolean().default(true),
  promptSeed: z.string().min(1).max(4000),
  businessContext: z.string().min(1).max(4000),
  targetAudience: SdkAgenticPromptContextSchema.shape.targetAudience.default(['solution-admin']),
  suggestedViews: z.array(z.enum(['list', 'detail', 'form', 'grid'])).min(1).default(['list', 'form']),
  resource: SdkResourceSchema,
  sampleRows: z.array(z.record(z.unknown())).default([]),
  tags: z.array(z.string().min(1).max(80)).default([]),
});
export type SdkResourceTemplate = z.infer<typeof SdkResourceTemplateSchema>;

export const SdkResourceTemplateDraftSchema = z.object({
  template: SdkResourceTemplateSchema,
  draft: SdkAgenticResourceDraftContractSchema,
});
export type SdkResourceTemplateDraft = z.infer<typeof SdkResourceTemplateDraftSchema>;

export const SdkResourceSchemaVersionSchema = z.object({
  versionId: IdSchema,
  resourceId: IdSchema,
  versionNumber: z.number().int().positive(),
  status: z.enum(['draft', 'published', 'archived']).default('draft'),
  summary: z.string().min(1).max(500),
  reviewed: SdkReviewedResourceContractSchema,
  createdAt: z.string().min(1),
});
export type SdkResourceSchemaVersion = z.infer<typeof SdkResourceSchemaVersionSchema>;

export const SdkDataRecordSchema = z.object({
  id: IdSchema,
  code: z.string().min(1).max(160),
  name: z.string().min(1).max(240),
  status: z.enum(['draft', 'active', 'inactive', 'archived']),
  effectiveAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  publishedAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
  payload: z.record(z.unknown()).default({}),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
});
export type SdkDataRecord = z.infer<typeof SdkDataRecordSchema>;

export const SdkImportDryRunRowSchema = z.object({
  index: z.number().int().nonnegative(),
  values: z.record(z.string()),
  status: z.enum(['ready', 'warning', 'error']),
  warnings: z.array(z.string()).default([]),
});
export type SdkImportDryRunRow = z.infer<typeof SdkImportDryRunRowSchema>;

export const SdkImportDryRunResultSchema = z.object({
  resourceId: IdSchema,
  detectedColumns: z.array(z.string()),
  mappedFields: z.record(z.string()),
  warnings: z.array(z.string()).default([]),
  rows: z.array(SdkImportDryRunRowSchema).default([]),
  readyCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
});
export type SdkImportDryRunResult = z.infer<typeof SdkImportDryRunResultSchema>;

export const SdkImportJobSchema = z.object({
  jobId: IdSchema,
  resourceId: IdSchema,
  mode: z.enum(['dry-run', 'commit']),
  status: z.enum(['completed', 'failed']),
  summary: z.string().min(1).max(500),
  createdAt: z.string().min(1),
  createdBy: z.string().min(1).max(160),
  readyCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
  importedCount: z.number().int().nonnegative().default(0),
  warnings: z.array(z.string()).default([]),
});
export type SdkImportJob = z.infer<typeof SdkImportJobSchema>;

export const SdkApprovalRequestSchema = z.object({
  requestId: IdSchema,
  resourceId: IdSchema,
  status: z.enum(['pending', 'approved', 'rejected']),
  summary: z.string().min(1).max(500),
  submittedBy: z.string().min(1).max(160),
  submittedAt: z.string().min(1),
  decidedBy: z.string().max(160).nullable(),
  decidedAt: z.string().nullable(),
  decisionReason: z.string().max(1000).nullable(),
});
export type SdkApprovalRequest = z.infer<typeof SdkApprovalRequestSchema>;

export const SdkAiValidationChecklistItemSchema = z.object({
  itemId: IdSchema,
  label: z.string().min(1).max(200),
  status: z.enum(['pass', 'warning', 'needs-input']),
  detail: z.string().min(1).max(500),
});
export type SdkAiValidationChecklistItem = z.infer<typeof SdkAiValidationChecklistItemSchema>;

export const SdkAiValidationReportSchema = z.object({
  reportId: IdSchema,
  resourceId: IdSchema,
  generatedAt: z.string().min(1),
  confidenceScore: z.number().min(0).max(1),
  assumptions: z.array(z.string().min(1).max(500)).default([]),
  missingInformation: z.array(z.string().min(1).max(500)).default([]),
  risks: z.array(z.string().min(1).max(500)).default([]),
  recommendedActions: z.array(z.string().min(1).max(500)).default([]),
  checklist: z.array(SdkAiValidationChecklistItemSchema).min(1),
});
export type SdkAiValidationReport = z.infer<typeof SdkAiValidationReportSchema>;

export const SdkPackManifestSchema = z.object({
  packId: IdSchema,
  code: z.string().min(1).max(120),
  label: z.string().min(1).max(200),
  version: z.string().min(1).max(80),
  source: z.enum(['internal', 'addon']),
  packKind: z.enum(['horizontal', 'vertical', 'solution']),
  dependencies: z.array(z.string().min(1).max(160)).default([]),
  surfaces: z.array(z.string().min(1).max(80)).default([]),
  capabilityRefs: z.object({
    dataQuery: z.array(z.string().min(1)).default([]),
    action: z.array(z.string().min(1)).default([]),
  }),
  defaultEnabled: z.boolean().default(false),
});
export type SdkPackManifest = z.infer<typeof SdkPackManifestSchema>;

export const SdkTenantPackStateSchema = z.object({
  packId: IdSchema,
  enabled: z.boolean(),
  compatible: z.boolean(),
  incompatibilities: z.array(z.string()).default([]),
});
export type SdkTenantPackState = z.infer<typeof SdkTenantPackStateSchema>;

export const SdkAuditEventSchema = z.object({
  eventId: IdSchema,
  resourceId: IdSchema,
  actor: z.string().min(1).max(160),
  actorType: z.enum(['human', 'ai', 'system']),
  action: z.enum([
    'draft_generated',
    'review_completed',
    'approval_submitted',
    'approval_approved',
    'approval_rejected',
    'record_created',
    'record_updated',
    'status_changed',
    'schema_version_published',
    'schema_version_rolled_back',
    'import_dry_run',
    'import_committed',
    'export_preview',
    'validation_report_generated',
  ]),
  timestamp: z.string().min(1),
  details: z.record(z.unknown()).default({}),
});
export type SdkAuditEvent = z.infer<typeof SdkAuditEventSchema>;
